import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ArrowRight } from "lucide-react";
import { Sheet, SheetTrigger, SheetContent, SheetTitle } from "./ui/sheet";
import { NAV } from "../lib/data";

export const Logo = ({ dark = false, className = "", testid = "logo-home", onClick }) => (
  <Link to="/" data-testid={testid} onClick={onClick} className={`inline-flex min-h-12 shrink-0 items-center gap-2 sm:gap-3 ${className}`}>
    <span className={`inline-flex shrink-0 items-center justify-center rounded-md overflow-hidden ${dark ? "bg-ws-bg p-1" : ""}`}>
      <img
        src="https://customer-assets-39nsmqrw.emergentagent.net/job_formula-to-product/artifacts/bc3msdvp_WhatsApp%20Image%202026-09-11%20at%2020.59.36%20%281%29.jpeg"
        alt="Wisperwick Studio"
        className="h-7 sm:h-8 w-auto object-contain mix-blend-multiply"
      />
    </span>
    <span className={`font-display text-base sm:text-xl whitespace-nowrap tracking-tight ${dark ? "text-ws-bg" : "text-ws-dark"}`}>
      Wisperwick <span className="italic">Studio</span>
    </span>
  </Link>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1280px)");
    const closeOnDesktop = () => { if (desktop.matches) setOpen(false); };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-40 border-b transition-colors duration-300 ${scrolled ? "bg-ws-bg/95 backdrop-blur-xl border-ws-border" : "bg-ws-bg border-transparent"}`}>
        <div className="max-w-[1600px] mx-auto px-5 sm:px-8 2xl:px-14 h-[72px] flex items-center justify-between gap-4">
          <Logo />
          <nav aria-label="Main navigation" className="hidden xl:flex items-center gap-4 2xl:gap-6">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`} aria-current={pathname === n.to ? "page" : undefined}
                className={`label text-[0.65rem] 2xl:text-[0.7rem] tracking-[0.08em] whitespace-nowrap min-h-12 inline-flex items-center transition-colors hover:text-ws-accent ${pathname === n.to ? "text-ws-accent" : "text-ws-text2"}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Link to="/request-a-quote" data-testid="nav-request-quote"
              className="hidden sm:inline-flex group items-center gap-2 label whitespace-nowrap min-h-12 bg-ws-dark text-ws-bg rounded-full px-5 py-3 hover:bg-ws-accentHover transition-colors">
              Request a Quote
              <ArrowRight className="w-3.5 h-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <SheetTrigger asChild>
              <button data-testid="menu-toggle" className="xl:hidden min-h-12 min-w-12 flex items-center justify-center text-ws-dark" aria-label="Open navigation menu">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
          </div>
        </div>
      </header>

      <SheetContent side="right" data-testid="mobile-menu" aria-describedby={undefined} data-lenis-prevent
        className="w-full sm:max-w-none h-[100dvh] border-0 bg-ws-dark text-ws-bg p-0 gap-0 flex flex-col">
        <SheetTitle className="sr-only">Site navigation</SheetTitle>
        <div className="h-[72px] shrink-0 flex items-center pl-5 sm:pl-8 pr-16">
          <Logo dark testid="mobile-logo-home" onClick={() => setOpen(false)} />
        </div>
        <nav aria-label="Mobile navigation" data-testid="mobile-menu-links" data-lenis-prevent
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 sm:px-8 py-4">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.to.slice(1)}`} onClick={() => setOpen(false)}
              aria-current={pathname === n.to ? "page" : undefined}
              className={`font-display text-2xl sm:text-3xl leading-tight min-h-12 py-3 block border-b border-ws-bg/10 hover:text-ws-accent transition-colors ${pathname === n.to ? "text-ws-accent" : "text-ws-bg"}`}>
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="mobile-menu-footer shrink-0 p-5 sm:px-8 border-t border-ws-bg/15">
          <Link to="/request-a-quote" data-testid="mobile-request-quote" onClick={() => setOpen(false)}
            className="w-full inline-flex min-h-12 items-center justify-center gap-2 label bg-ws-accentHover text-white rounded-full py-4 transition-colors hover:bg-ws-accent">
            Request a Quote <ArrowRight className="w-4 h-4 shrink-0" />
          </Link>
        </div>
      </SheetContent>

      {!open && pathname !== "/request-a-quote" && (
        <Link to="/request-a-quote" data-testid="sticky-mobile-quote"
          className="mobile-quote-cta sm:hidden fixed inset-x-4 z-30 inline-flex min-h-12 items-center justify-center gap-2 label bg-ws-dark text-ws-bg rounded-full py-4 shadow-xl transition-colors hover:bg-ws-accentHover">
          Request a Quote <ArrowRight className="w-4 h-4 shrink-0" />
        </Link>
      )}
    </Sheet>
  );
}
